import { createFileRoute, redirect } from '@tanstack/react-router';
import { Loader2, Wrench } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { formatDateOnly } from '@/lib/dates/date-only';
import { hasLivePortalSession } from '@/stores/portalAuth.store';
import { usePortalEquipment, type PortalEquipment } from '@/hooks/portal/usePortalEquipment';
import { PortalShell } from '@/components/portal/PortalShell';
import { PortalStatusPill, type PortalPillTone } from '@/components/portal/PortalStatusPill';

export const Route = createFileRoute('/(portal)/portal/equipment/')({
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalEquipmentPage,
});

const CONDITION: Record<string, { label: string; tone: PortalPillTone }> = {
  EXCELLENT: { label: 'Excellent', tone: 'success' },
  GOOD: { label: 'Good', tone: 'success' },
  FAIR: { label: 'Fair', tone: 'info' },
  POOR: { label: 'Poor', tone: 'accent' },
  CRITICAL: { label: 'Needs replacing', tone: 'accent' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
};

function conditionOf(c: string): { label: string; tone: PortalPillTone } {
  return CONDITION[c] ?? { label: c.toLowerCase(), tone: 'neutral' };
}

function PortalEquipmentPage() {
  const equipment = usePortalEquipment();

  return (
    <PortalShell>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Equipment</h1>
        <p className="text-sm text-muted-foreground">
          What&apos;s installed at your property — age, warranty and condition, kept current on
          every visit.
        </p>
      </div>

      {equipment.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : equipment.isError ? (
        <Card>
          <CardContent className="space-y-3 py-6 text-sm text-muted-foreground">
            <p>We couldn&apos;t load your equipment. Please try again.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void equipment.refetch()}
              disabled={equipment.isFetching}
            >
              {equipment.isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : equipment.data.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            We don&apos;t have any equipment on file for you yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {equipment.data.map((e) => (
            <EquipmentCard key={e.id} equipment={e} />
          ))}
        </div>
      )}
    </PortalShell>
  );
}

/** Age vs expected life → { label, pct 0..100, tone }, or null when unknown. */
function lifeOf(e: PortalEquipment): { label: string; pct: number; danger: boolean } | null {
  if (!e.installDate || !e.expectedLifespanYears || e.expectedLifespanYears <= 0) return null;
  const installed = new Date(e.installDate).getTime();
  const ageYears = (Date.now() - installed) / (365.25 * 86_400_000);
  const pct = Math.max(0, Math.min(100, Math.round((ageYears / e.expectedLifespanYears) * 100)));
  return {
    label: `Age ${Math.round(ageYears)} of ~${e.expectedLifespanYears} year life`,
    pct,
    danger: pct >= 85,
  };
}

function EquipmentCard({ equipment }: { equipment: PortalEquipment }) {
  const condition = conditionOf(equipment.condition);
  const life = lifeOf(equipment);
  const makeModel = [equipment.make, equipment.model].filter(Boolean).join(' ');
  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex items-start gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <Wrench className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{equipment.name}</p>
            {makeModel ? (
              <p className="truncate text-xs text-muted-foreground">{makeModel}</p>
            ) : null}
          </div>
          <PortalStatusPill label={condition.label} tone={condition.tone} />
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <Field label="Serial" value={equipment.serialNumber} />
          <Field
            label="Installed"
            value={equipment.installDate ? formatDateOnly(equipment.installDate) : null}
          />
          <Field label="Parts warranty" value={warrantyLabel(equipment.warrantyPartsEnd)} />
          <Field label="Labor warranty" value={warrantyLabel(equipment.warrantyLaborEnd)} />
        </dl>

        {life ? (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{life.label}</span>
              <span className={life.danger ? 'font-medium text-warning' : ''}>{life.pct}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full rounded-full ${life.danger ? 'bg-warning' : 'bg-foreground/60'}`}
                style={{ width: `${life.pct}%` }}
              />
            </div>
          </div>
        ) : null}

        {equipment.conditionNotes ? (
          <p className="border-t border-border pt-2 text-xs text-muted-foreground">
            {equipment.conditionNotes}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function warrantyLabel(end: string | null): string | null {
  if (!end) return null;
  // Compare CALENDAR days, not a UTC-midnight instant vs. `Date.now()`: the
  // warranty is still "through" its own last day for a viewer west of UTC.
  const [y, m, d] = end.slice(0, 10).split('-').map(Number);
  const now = new Date();
  const active =
    Date.UTC(y, m - 1, d) >= Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return `${active ? 'through' : 'expired'} ${formatDateOnly(end)}`;
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="truncate font-medium">{value ?? '—'}</dd>
    </div>
  );
}
